// EvolutionsPOC.js — Documentation Fonctionnelle : POC « Évolutions » (module Environnement).
// Aperçu INTERACTIF et FIDÈLE à la disposition de l'écran réel de Gestion des Évolutions :
// en-tête + bascule liste/grille, KPIs, barre de filtres (recherche, fonction, statut, priorité),
// tableau triable OU grille de cartes, et tiroir de détail latéral (stepper de workflow + sections).
// Les actions sont SIMULÉES localement (démo, aucun appel réseau).
// Source de vérité : automata-studio-frontend [AUT][06]-Evolutions.js + automata_environment EvolutionController.
import React, { useState } from 'react';
import {
  Plus, Activity, AlertTriangle, Zap, ThumbsUp, User, Check,
  CheckCircle, XCircle, X, Info, ArrowRight, Flag,
  List as ListIcon, Grid as GridIcon, RefreshCw, Edit2, Trash2, ChevronDown,
} from 'lucide-react';
import { Card, CardContent, CardHeader, CardTitle, Button, Badge, Tabs, Icon, SearchBar, EmptyState } from '../ui';

// ---- Référentiels (libellés exacts de l'écran réel) ------------------------
// Workflow nominal : REQUESTED → APPROVED → ASSIGNED → IN_PROGRESS → IMPLEMENTED → TESTED → DEPLOYED (+ REJECTED).
const WORKFLOW = ['REQUESTED', 'APPROVED', 'ASSIGNED', 'IN_PROGRESS', 'IMPLEMENTED', 'TESTED', 'DEPLOYED'];

const STATUS = {
  REQUESTED:   { label: 'Demandée',    color: '#3B82F6', icon: 'Send' },
  APPROVED:    { label: 'Approuvée',   color: '#10B981', icon: 'ThumbsUp' },
  ASSIGNED:    { label: 'Assignée',    color: '#8B5CF6', icon: 'User' },
  IN_PROGRESS: { label: 'En cours',    color: '#F59E0B', icon: 'Activity' },
  IMPLEMENTED: { label: 'Implémentée', color: '#14B8A6', icon: 'Check' },
  TESTED:      { label: 'Testée',      color: '#06B6D4', icon: 'CheckCircle' },
  DEPLOYED:    { label: 'Déployée',    color: '#22C55E', icon: 'Star' },
  REJECTED:    { label: 'Rejetée',     color: '#EF4444', icon: 'X' },
};
const statusOf = (s) => STATUS[s] || { label: s, color: '#6B7280', icon: 'Info' };
// Transition « suivante » simulée dans le tiroir (le bouton avance d'une étape).
const NEXT = { REQUESTED: 'APPROVED', APPROVED: 'ASSIGNED', ASSIGNED: 'IN_PROGRESS', IN_PROGRESS: 'IMPLEMENTED', IMPLEMENTED: 'TESTED', TESTED: 'DEPLOYED' };

const PRIORITY = {
  CRITICAL: { label: 'Critique', color: '#DC2626' },
  HIGH:     { label: 'Élevée',   color: '#F97316' },
  MEDIUM:   { label: 'Moyenne',  color: '#F59E0B' },
  LOW:      { label: 'Faible',   color: '#3B82F6' },
};
const prioOf = (p) => PRIORITY[p] || { label: p, color: '#6B7280' };

const TYPE = {
  FEATURE:     { label: 'Nouvelle fonctionnalité', color: '#8B5CF6', icon: 'Zap' },
  ENHANCEMENT: { label: 'Amélioration',            color: '#10B981', icon: 'Star' },
  OPTIMIZATION:{ label: 'Optimisation',            color: '#06B6D4', icon: 'Settings' },
  REFACTORING: { label: 'Refactoring',             color: '#6B7280', icon: 'Wrench' },
};
const typeOf = (t) => TYPE[t] || { label: t, color: '#6B7280', icon: 'Tag' };

const SAMPLE = [
  { id: 1, title: 'Export PDF des campagnes', description: 'Permettre l\'export d\'un rapport de campagne au format PDF avec graphiques.', type: 'FEATURE', priority: 'HIGH', status: 'IN_PROGRESS', functionName: 'Reporting', requestDate: '2026-05-28', requestedBy: 'amine', assignedTo: 'youssef', implementedBy: null, implementation: '' },
  { id: 2, title: 'Filtrage multi-statuts sur les tâches', description: 'Ajouter un filtre combiné statut + type dans la liste des tâches.', type: 'ENHANCEMENT', priority: 'MEDIUM', status: 'REQUESTED', functionName: 'Tâches', requestDate: '2026-06-04', requestedBy: 'sara', assignedTo: null, implementedBy: null, implementation: '' },
  { id: 3, title: 'Cache des résultats de rejeu', description: 'Mettre en cache les dernières exécutions pour accélérer l\'affichage.', type: 'OPTIMIZATION', priority: 'CRITICAL', status: 'DEPLOYED', functionName: 'Exécutions', requestDate: '2026-04-15', requestedBy: 'admin', assignedTo: 'youssef', implementedBy: 'youssef', implementation: 'Cache Redisson ajouté sur le port de lecture des exécutions, TTL 5 min.' },
  { id: 4, title: 'Nettoyage du module Agents', description: 'Refactoring du service de connectivité agent (dette technique).', type: 'REFACTORING', priority: 'LOW', status: 'ASSIGNED', functionName: 'Agents', requestDate: '2026-06-09', requestedBy: 'ops', assignedTo: 'amine', implementedBy: null, implementation: '' },
  { id: 5, title: 'Webhook de notification fin de campagne', description: 'Appel HTTP sortant à la clôture d\'une campagne.', type: 'FEATURE', priority: 'MEDIUM', status: 'REJECTED', functionName: 'Campagnes', requestDate: '2026-05-02', requestedBy: 'sara', assignedTo: null, implementedBy: null, implementation: '' },
];

const FUNCTIONS = ['Reporting', 'Tâches', 'Exécutions', 'Agents', 'Campagnes'];

// ---- Petits sous-composants -------------------------------------------------
const StatusBadge = ({ status }) => { const s = statusOf(status); return <Badge color={s.color}><Icon name={s.icon} className="h-3 w-3" />{s.label}</Badge>; };
const PriorityBadge = ({ priority }) => { const p = prioOf(priority); return <Badge color={p.color}><Flag className="h-3 w-3" />{p.label}</Badge>; };
const TypeBadge = ({ type }) => { const t = typeOf(type); return <Badge color={t.color}><Icon name={t.icon} className="h-3 w-3" />{t.label}</Badge>; };

const GuideStep = ({ n, children }) => (
  <li className="flex items-start gap-3">
    <span className="shrink-0 w-6 h-6 rounded-full bg-blue-500 text-white text-xs font-bold flex items-center justify-center">{n}</span>
    <span className="text-sm text-gray-700 dark:text-gray-300">{children}</span>
  </li>
);

const InfoRow = ({ label, value }) => (
  <div>
    <p className="text-[10px] uppercase tracking-wider text-gray-400 dark:text-gray-500">{label}</p>
    <p className="text-sm text-gray-900 dark:text-white mt-0.5">{value || '—'}</p>
  </div>
);

// Stepper de workflow (réplique du composant WorkflowStepper de l'écran réel).
const Stepper = ({ status }) => {
  const idx = WORKFLOW.indexOf(status);
  const rejected = status === 'REJECTED';
  return (
    <div className="flex items-center">
      {WORKFLOW.map((step, i) => {
        const done = !rejected && i <= idx;
        const current = step === status;
        const s = statusOf(step);
        return (
          <React.Fragment key={step}>
            <div className="flex flex-col items-center" title={s.label}>
              <div className={`w-7 h-7 rounded-full flex items-center justify-center text-[10px] font-bold border-2 transition-all ${current ? 'scale-110' : ''}`}
                style={done ? { backgroundColor: `${s.color}1A`, color: s.color, borderColor: `${s.color}55` } : undefined}>
                {done ? <Check className="h-3 w-3" /> : <span className="text-gray-400">{i + 1}</span>}
              </div>
              <span className="text-[9px] mt-1 text-gray-500 dark:text-gray-400">{s.label.substring(0, 5)}</span>
            </div>
            {i < WORKFLOW.length - 1 && <div className={`flex-1 h-0.5 rounded-full min-w-[10px] mx-0.5 ${!rejected && i < idx ? 'bg-emerald-400' : 'bg-gray-200 dark:bg-gray-700'}`} />}
          </React.Fragment>
        );
      })}
      {rejected && (
        <div className="flex flex-col items-center ml-2" title="Rejetée">
          <div className="w-7 h-7 rounded-full flex items-center justify-center" style={{ backgroundColor: '#EF4444' + '1A', color: '#EF4444', border: '2px solid #EF444455' }}><X className="h-3 w-3" /></div>
          <span className="text-[9px] mt-1 text-red-500">Rej.</span>
        </div>
      )}
    </div>
  );
};

// ---- Page -------------------------------------------------------------------
const EvolutionsPOC = () => {
  const [evolutions, setEvolutions] = useState(SAMPLE);
  const [view, setView] = useState('table');        // 'table' | 'grid'
  const [search, setSearch] = useState('');
  const [functionFilter, setFunctionFilter] = useState('all');
  const [statusFilter, setStatusFilter] = useState('all');
  const [priorityFilter, setPriorityFilter] = useState('all');
  const [drawerId, setDrawerId] = useState(null);   // id de l'évolution ouverte dans le tiroir
  const [flash, setFlash] = useState(null);         // message éphémère simulé

  const selected = evolutions.find((e) => e.id === drawerId) || null;
  const filtered = evolutions.filter((e) =>
    (functionFilter === 'all' || e.functionName === functionFilter) &&
    (statusFilter === 'all' || e.status === statusFilter) &&
    (priorityFilter === 'all' || e.priority === priorityFilter) &&
    (`${e.title} ${e.description} ${e.functionName}`.toLowerCase().includes(search.toLowerCase()))
  );

  const stats = {
    total: evolutions.length,
    critical: evolutions.filter((e) => e.priority === 'CRITICAL').length,
    inProgress: evolutions.filter((e) => ['ASSIGNED', 'IN_PROGRESS'].includes(e.status)).length,
    deployed: evolutions.filter((e) => e.status === 'DEPLOYED').length,
  };

  const notify = (msg) => { setFlash(msg); window.setTimeout(() => setFlash(null), 2200); };

  // Transition de statut SIMULÉE : avance l'évolution ouverte à l'étape suivante.
  const advance = () => {
    if (!selected) return;
    const next = NEXT[selected.status];
    if (!next) return;
    setEvolutions((prev) => prev.map((e) => e.id === drawerId ? { ...e, status: next } : e));
    notify(`Statut passé à « ${statusOf(next).label} »`);
  };
  const reject = () => {
    if (!selected || selected.status === 'DEPLOYED' || selected.status === 'REJECTED') return;
    setEvolutions((prev) => prev.map((e) => e.id === drawerId ? { ...e, status: 'REJECTED' } : e));
    notify('Évolution rejetée');
  };
  // Scénario « Créer » simulé : ajoute une demande en statut Demandée et ouvre son tiroir.
  const simulateCreate = () => {
    const id = Math.max(0, ...evolutions.map((e) => e.id)) + 1;
    const created = { id, title: `Nouvelle évolution #${id}`, description: 'Demande créée depuis le guide (démo).', type: 'FEATURE', priority: 'MEDIUM', status: 'REQUESTED', functionName: 'Tâches', requestDate: '2026-06-15', requestedBy: 'vous', assignedTo: null, implementedBy: null, implementation: '' };
    setEvolutions((prev) => [created, ...prev]);
    setDrawerId(id); notify('Évolution créée (Demandée)');
  };
  const simulateRefresh = () => notify('Liste actualisée');
  const simulateDelete = (e) => { setEvolutions((prev) => prev.filter((x) => x.id !== e.id)); if (drawerId === e.id) setDrawerId(null); notify(`« ${e.title} » supprimée`); };

  // Faux contrôle « select » (sans réseau) reproduisant la barre de filtres de l'écran réel.
  const FilterSelect = ({ label, value, onChange, options }) => (
    <div className="relative">
      <select value={value} onChange={(e) => onChange(e.target.value)}
        className="appearance-none pl-3 pr-8 py-2 text-sm rounded-lg border border-gray-300 dark:border-gray-600 bg-white dark:bg-gray-800 text-gray-700 dark:text-gray-200 focus:ring-2 focus:ring-blue-500 focus:outline-none">
        {options.map((o) => <option key={o.value} value={o.value}>{o.label}</option>)}
      </select>
      <ChevronDown className="absolute right-2 top-1/2 -translate-y-1/2 h-4 w-4 text-gray-400 pointer-events-none" />
      <span className="absolute -top-2 left-2 px-1 text-[10px] bg-white dark:bg-gray-800 text-gray-400">{label}</span>
    </div>
  );

  return (
    <div className="space-y-8">
      {/* ===== Intro ===== */}
      <div>
        <div className="flex items-center gap-2 flex-wrap">
          <h1 className="text-2xl font-bold text-gray-900 dark:text-white">Évolutions</h1>
          <Badge color="#3B82F6">Module Environnement</Badge>
          <Badge color="#F59E0B">POC interactif</Badge>
        </div>
        <p className="mt-2 text-gray-600 dark:text-gray-300">Aperçu interactif de l'écran <strong>Gestion des Évolutions</strong> (démo, actions simulées localement).</p>
      </div>

      {/* ===== Aperçu fidèle de l'IHM ===== */}
      <Card className="overflow-hidden">
        {/* En-tête type app : titre + bascule liste/grille + actualiser + nouvelle évolution */}
        <div className="px-5 py-4 bg-gradient-to-r from-blue-600 to-indigo-700 text-white flex items-center justify-between gap-3 flex-wrap">
          <div className="flex items-center gap-3">
            <div className="w-10 h-10 rounded-xl bg-white/20 flex items-center justify-center"><Activity className="h-5 w-5" /></div>
            <div>
              <h2 className="text-lg font-bold leading-tight">Gestion des Évolutions</h2>
              <p className="text-xs text-white/80">Planification et suivi des évolutions</p>
            </div>
          </div>
          <div className="flex items-center gap-2">
            <div className="flex bg-white/10 rounded-xl p-1">
              {[{ k: 'table', I: ListIcon }, { k: 'grid', I: GridIcon }].map(({ k, I }) => (
                <button key={k} onClick={() => setView(k)} className={`p-2 rounded-lg transition-all ${view === k ? 'bg-white/20 text-white' : 'text-white/60 hover:text-white'}`}><I className="h-4 w-4" /></button>
              ))}
            </div>
            <button onClick={simulateRefresh} className="p-2 rounded-xl bg-white/10 hover:bg-white/20 text-white"><RefreshCw className="h-4 w-4" /></button>
            <Button onClick={simulateCreate} className="bg-white/20 hover:bg-white/30 text-white"><Plus className="h-4 w-4" />Nouvelle Évolution</Button>
          </div>
        </div>

        {/* KPIs */}
        <div className="grid grid-cols-2 lg:grid-cols-4 gap-3 p-4 border-b border-gray-200 dark:border-gray-700">
          {[
            { label: 'Total', value: stats.total, color: '#3B82F6', icon: Activity },
            { label: 'Critiques', value: stats.critical, color: '#EF4444', icon: AlertTriangle },
            { label: 'En cours', value: stats.inProgress, color: '#F59E0B', icon: Zap },
            { label: 'Déployées', value: stats.deployed, color: '#22C55E', icon: CheckCircle },
          ].map((k) => (
            <div key={k.label} className="rounded-xl p-3 flex items-center justify-between" style={{ backgroundColor: `${k.color}14`, border: `1px solid ${k.color}33` }}>
              <div>
                <div className="text-2xl font-bold" style={{ color: k.color }}>{k.value}</div>
                <div className="text-xs text-gray-500 dark:text-gray-400">{k.label}</div>
              </div>
              <k.icon className="h-6 w-6" style={{ color: k.color }} />
            </div>
          ))}
        </div>

        {/* Barre de filtres : recherche + fonction + statut + priorité (comme l'écran réel) */}
        <div className="flex flex-wrap items-center gap-3 p-4 border-b border-gray-200 dark:border-gray-700">
          <div className="flex-1 min-w-[200px] max-w-sm"><SearchBar value={search} onChange={setSearch} placeholder="Rechercher..." /></div>
          <FilterSelect label="Fonction" value={functionFilter} onChange={setFunctionFilter}
            options={[{ value: 'all', label: 'Toutes' }, ...FUNCTIONS.map((f) => ({ value: f, label: f }))]} />
          <FilterSelect label="Statut" value={statusFilter} onChange={setStatusFilter}
            options={[{ value: 'all', label: 'Tous' }, ...WORKFLOW.concat('REJECTED').map((s) => ({ value: s, label: statusOf(s).label }))]} />
          <FilterSelect label="Priorité" value={priorityFilter} onChange={setPriorityFilter}
            options={[{ value: 'all', label: 'Toutes' }, ...Object.keys(PRIORITY).map((p) => ({ value: p, label: prioOf(p).label }))]} />
        </div>

        {/* Message éphémère simulé */}
        {flash && (
          <div className="mx-4 mt-4 flex items-center gap-2 p-2.5 rounded-lg bg-emerald-50 dark:bg-emerald-500/10 border border-emerald-200 dark:border-emerald-500/30 text-emerald-700 dark:text-emerald-300 text-sm">
            <CheckCircle className="h-4 w-4" />{flash}
          </div>
        )}

        {/* ===== Contenu : tableau OU grille ===== */}
        <div className="p-4">
          {filtered.length === 0 ? (
            <EmptyState icon={Activity} title="Aucune évolution trouvée" message="Modifiez la recherche ou les filtres." />
          ) : view === 'table' ? (
            <div className="overflow-x-auto rounded-lg border border-gray-200 dark:border-gray-700">
              <table className="w-full text-sm">
                <thead>
                  <tr className="border-b border-gray-200 dark:border-gray-700 text-gray-500 dark:text-gray-400">
                    <th className="px-4 py-3 text-left text-xs font-medium uppercase tracking-wider w-12">#</th>
                    <th className="px-4 py-3 text-left text-xs font-medium uppercase tracking-wider">Titre</th>
                    <th className="px-4 py-3 text-left text-xs font-medium uppercase tracking-wider hidden lg:table-cell">Type</th>
                    <th className="px-4 py-3 text-left text-xs font-medium uppercase tracking-wider hidden lg:table-cell">Fonction</th>
                    <th className="px-4 py-3 text-left text-xs font-medium uppercase tracking-wider">Priorité</th>
                    <th className="px-4 py-3 text-left text-xs font-medium uppercase tracking-wider">Statut</th>
                    <th className="px-4 py-3 text-left text-xs font-medium uppercase tracking-wider hidden md:table-cell">Demandée</th>
                    <th className="px-4 py-3 text-left text-xs font-medium uppercase tracking-wider hidden md:table-cell">Assigné</th>
                    <th className="px-4 py-3 text-right text-xs font-medium uppercase tracking-wider" />
                  </tr>
                </thead>
                <tbody className="divide-y divide-gray-100 dark:divide-gray-700/50">
                  {filtered.map((e) => (
                    <tr key={e.id} onClick={() => setDrawerId(e.id)} className="hover:bg-gray-50 dark:hover:bg-gray-700/30 cursor-pointer">
                      <td className="px-4 py-3 text-gray-400">{e.id}</td>
                      <td className="px-4 py-3">
                        <p className="font-medium text-gray-900 dark:text-white">{e.title}</p>
                        {e.description && <p className="text-xs text-gray-400 dark:text-gray-500 truncate max-w-[220px]">{e.description}</p>}
                      </td>
                      <td className="px-4 py-3 hidden lg:table-cell"><TypeBadge type={e.type} /></td>
                      <td className="px-4 py-3 hidden lg:table-cell">
                        <span className="px-2 py-0.5 rounded-full text-xs font-medium bg-teal-100 dark:bg-teal-900/30 text-teal-700 dark:text-teal-300">{e.functionName}</span>
                      </td>
                      <td className="px-4 py-3"><PriorityBadge priority={e.priority} /></td>
                      <td className="px-4 py-3"><StatusBadge status={e.status} /></td>
                      <td className="px-4 py-3 text-xs text-gray-500 dark:text-gray-400 hidden md:table-cell">{e.requestDate}</td>
                      <td className="px-4 py-3 hidden md:table-cell">
                        {e.assignedTo
                          ? <span className="inline-flex items-center gap-1 px-2 py-0.5 rounded-full text-xs font-medium border border-gray-200 dark:border-gray-600 text-gray-700 dark:text-gray-300"><User className="h-3 w-3" />{e.assignedTo}</span>
                          : <span className="text-xs text-gray-400">Non assignée</span>}
                      </td>
                      <td className="px-4 py-3">
                        <div className="flex items-center justify-end gap-1" onClick={(ev) => ev.stopPropagation()}>
                          <button onClick={() => setDrawerId(e.id)} className="p-1.5 rounded-lg text-gray-400 hover:bg-gray-100 dark:hover:bg-gray-700" title="Modifier"><Edit2 className="h-4 w-4" /></button>
                          <button onClick={() => simulateDelete(e)} className="p-1.5 rounded-lg text-red-400 hover:bg-red-50 dark:hover:bg-red-500/10" title="Supprimer"><Trash2 className="h-4 w-4" /></button>
                        </div>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          ) : (
            <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4">
              {filtered.map((e) => (
                <button key={e.id} onClick={() => setDrawerId(e.id)}
                  className="relative text-left rounded-xl border border-gray-200 dark:border-gray-700 hover:border-blue-400 dark:hover:border-blue-500 transition-all flex flex-col">
                  <div className="absolute top-3 right-3"><PriorityBadge priority={e.priority} /></div>
                  <div className="p-4 flex-1">
                    <h3 className="text-sm font-semibold text-gray-900 dark:text-white mb-2 pr-24">{e.title}</h3>
                    <div className="flex flex-wrap gap-1.5 mb-3"><StatusBadge status={e.status} /><TypeBadge type={e.type} /></div>
                    {e.description && <p className="text-xs text-gray-500 dark:text-gray-400 mb-3 line-clamp-2">{e.description}</p>}
                    <div className="grid grid-cols-2 gap-2">
                      <InfoRow label="Demandée" value={e.requestDate} />
                      <InfoRow label="Fonction" value={e.functionName} />
                      <div className="col-span-2"><InfoRow label="Assigné à" value={e.assignedTo || 'Non assignée'} /></div>
                    </div>
                  </div>
                  <div className="flex items-center justify-between px-4 py-2.5 border-t border-gray-100 dark:border-gray-700/50">
                    <div className="flex items-center gap-1" onClick={(ev) => ev.stopPropagation()}>
                      <button onClick={() => setDrawerId(e.id)} className="p-1.5 rounded-lg text-gray-400 hover:bg-gray-100 dark:hover:bg-gray-700"><Edit2 className="h-4 w-4" /></button>
                      <button onClick={() => simulateDelete(e)} className="p-1.5 rounded-lg text-red-400 hover:bg-red-50 dark:hover:bg-red-500/10"><Trash2 className="h-4 w-4" /></button>
                    </div>
                    <span className="text-xs font-medium text-blue-600 dark:text-blue-400 inline-flex items-center gap-1">Détails <Info className="h-3 w-3" /></span>
                  </div>
                </button>
              ))}
            </div>
          )}
        </div>
      </Card>

      {/* ===== Tiroir latéral de détail (simulé en overlay) ===== */}
      {selected && (
        <div className="fixed inset-0 z-50 flex justify-end">
          <div className="absolute inset-0 bg-black/30" onClick={() => setDrawerId(null)} />
          <div className="relative w-full max-w-md h-full bg-white dark:bg-gray-800 shadow-2xl flex flex-col">
            {/* En-tête du tiroir */}
            <div className="px-5 py-4 bg-gradient-to-r from-emerald-500 to-green-700 text-white flex items-center justify-between">
              <h2 className="text-lg font-semibold">Détails</h2>
              <button onClick={() => setDrawerId(null)} className="p-1.5 rounded-lg bg-white/10 hover:bg-white/20"><X className="h-4 w-4" /></button>
            </div>

            <div className="flex-1 overflow-y-auto p-5 space-y-4">
              {/* Bloc titre + badges */}
              <div>
                <div className="flex items-center gap-2 mb-2 flex-wrap"><StatusBadge status={selected.status} /><PriorityBadge priority={selected.priority} /></div>
                <h3 className="text-base font-semibold text-gray-900 dark:text-white mb-1">{selected.title}</h3>
                <p className="text-sm text-gray-500 dark:text-gray-400 whitespace-pre-wrap">{selected.description || 'Aucune description'}</p>
                <div className="grid grid-cols-2 gap-3 mt-3">
                  <InfoRow label="ID" value={selected.id} />
                  <div>
                    <p className="text-[10px] uppercase tracking-wider text-gray-400 dark:text-gray-500 mb-1">Type</p>
                    <TypeBadge type={selected.type} />
                  </div>
                </div>
              </div>

              {/* Progression (stepper) */}
              <div className="border-t border-gray-200 dark:border-gray-700 pt-4">
                <h4 className="text-xs font-semibold text-gray-500 dark:text-gray-400 uppercase tracking-wider mb-3">Progression</h4>
                <div className="overflow-x-auto"><Stepper status={selected.status} /></div>
              </div>

              {/* Informations (dates) */}
              <div className="border-t border-gray-200 dark:border-gray-700 pt-4">
                <h4 className="text-xs font-semibold text-gray-500 dark:text-gray-400 uppercase tracking-wider mb-3">Informations</h4>
                <div className="grid grid-cols-2 gap-3">
                  <InfoRow label="Demandée le" value={selected.requestDate} />
                  <InfoRow label="Implémentée le" value={selected.status === 'IMPLEMENTED' || selected.implementation ? selected.requestDate : '—'} />
                </div>
              </div>

              {/* Fonction */}
              <div className="border-t border-gray-200 dark:border-gray-700 pt-4">
                <h4 className="text-xs font-semibold text-gray-500 dark:text-gray-400 uppercase tracking-wider mb-2">Fonction</h4>
                <div className="bg-gray-50 dark:bg-gray-700/50 rounded-xl border border-gray-200 dark:border-gray-700 p-3">
                  <p className="text-sm font-medium text-gray-900 dark:text-white">{selected.functionName}</p>
                  <p className="text-xs text-gray-500 dark:text-gray-400 mt-1">Fonction métier rattachée à la demande.</p>
                </div>
              </div>

              {/* Implémentation (si présente) */}
              {selected.implementation && (
                <div className="border-t border-gray-200 dark:border-gray-700 pt-4">
                  <h4 className="text-xs font-semibold text-gray-500 dark:text-gray-400 uppercase tracking-wider mb-2">Implémentation</h4>
                  <div className="bg-gray-50 dark:bg-gray-700/50 rounded-xl border border-gray-200 dark:border-gray-700 p-3">
                    <p className="text-sm text-gray-700 dark:text-gray-300 whitespace-pre-wrap">{selected.implementation}</p>
                  </div>
                </div>
              )}

              {/* Personnes */}
              <div className="border-t border-gray-200 dark:border-gray-700 pt-4">
                <h4 className="text-xs font-semibold text-gray-500 dark:text-gray-400 uppercase tracking-wider mb-3">Personnes</h4>
                <div className="grid grid-cols-2 gap-3">
                  <InfoRow label="Demandée par" value={selected.requestedBy} />
                  <InfoRow label="Assignée à" value={selected.assignedTo || 'Non assignée'} />
                  <InfoRow label="Implémentée par" value={selected.implementedBy} />
                </div>
              </div>

              {/* Transition de statut simulée */}
              <div className="border-t border-gray-200 dark:border-gray-700 pt-4">
                <h4 className="text-xs font-semibold text-gray-500 dark:text-gray-400 uppercase tracking-wider mb-3">Transition de statut (démo)</h4>
                <div className="flex flex-wrap gap-2">
                  {NEXT[selected.status] ? (
                    <Button onClick={advance} className="bg-gradient-to-r from-emerald-500 to-green-700 text-white">
                      <ArrowRight className="h-4 w-4" />Passer à « {statusOf(NEXT[selected.status]).label} »
                    </Button>
                  ) : (
                    <Badge color={statusOf(selected.status).color}><Icon name={statusOf(selected.status).icon} className="h-3 w-3" />État final : {statusOf(selected.status).label}</Badge>
                  )}
                  {selected.status !== 'DEPLOYED' && selected.status !== 'REJECTED' && (
                    <Button variant="ghost" onClick={reject} className="text-red-600"><X className="h-4 w-4" />Rejeter</Button>
                  )}
                </div>
              </div>
            </div>

            {/* Pied du tiroir : actions contextuelles selon le statut */}
            <div className="flex items-center justify-between px-5 py-4 border-t border-gray-200 dark:border-gray-700 bg-gray-50 dark:bg-gray-800/50">
              <Button variant="outline" onClick={() => notify('Édition (démo)')}><Edit2 className="h-4 w-4" />Modifier</Button>
              <div className="flex items-center gap-2">
                {selected.status === 'REQUESTED' && <Button onClick={advance} className="bg-gradient-to-r from-emerald-500 to-green-700 text-white"><ThumbsUp className="h-4 w-4" />Approuver</Button>}
                {selected.status === 'APPROVED' && <Button onClick={advance} className="bg-gradient-to-r from-purple-500 to-violet-700 text-white"><User className="h-4 w-4" />Assigner</Button>}
                {selected.status === 'IN_PROGRESS' && <Button onClick={advance} className="bg-gradient-to-r from-amber-500 to-orange-600 text-white"><Check className="h-4 w-4" />Implémenter</Button>}
              </div>
            </div>
          </div>
        </div>
      )}

      {/* ===== Guide utilisateur ===== */}
      <div>
        <h2 className="text-xl font-bold text-gray-900 dark:text-white mb-4">Guide utilisateur</h2>
        <div className="grid grid-cols-1 lg:grid-cols-3 gap-4">
          <Card><CardHeader><CardTitle className="flex items-center gap-2"><Plus className="h-5 w-5 text-blue-500" />Créer une évolution</CardTitle></CardHeader><CardContent>
            <ol className="space-y-2">
              <GuideStep n={1}>Cliquer sur <strong>« Nouvelle Évolution »</strong> (en haut à droite). <button onClick={simulateCreate} className="text-blue-600 dark:text-blue-400 underline">Essayer</button></GuideStep>
              <GuideStep n={2}>Renseigner le <strong>Titre</strong> et la <strong>Fonction</strong> (champs obligatoires).</GuideStep>
              <GuideStep n={3}>Choisir le <strong>Type</strong> (Fonctionnalité, Amélioration, Optimisation, Refactoring) et la <strong>Priorité</strong>.</GuideStep>
              <GuideStep n={4}>Décrire le besoin puis valider : la demande naît au statut <strong>Demandée</strong>.</GuideStep>
            </ol>
          </CardContent></Card>

          <Card><CardHeader><CardTitle className="flex items-center gap-2"><Activity className="h-5 w-5 text-blue-500" />Suivre une évolution</CardTitle></CardHeader><CardContent>
            <ol className="space-y-2">
              <GuideStep n={1}>Cliquer sur une ligne du <strong>tableau</strong> (ou une carte en vue grille) pour ouvrir le <strong>tiroir de détail</strong>. <button onClick={() => setDrawerId(SAMPLE[0].id)} className="text-blue-600 dark:text-blue-400 underline">Voir</button></GuideStep>
              <GuideStep n={2}>Consulter la section <strong>Progression</strong> pour visualiser le <strong>stepper</strong> du workflow.</GuideStep>
              <GuideStep n={3}>Faire avancer : <strong>Approuver</strong> → <strong>Assigner</strong> → <strong>En cours</strong> → <strong>Implémenter</strong> → <strong>Tester</strong>.</GuideStep>
              <GuideStep n={4}>Utiliser la <strong>barre de filtres</strong> (fonction, statut, priorité) et la recherche, ou la bascule <strong>liste / grille</strong>.</GuideStep>
            </ol>
          </CardContent></Card>

          <Card><CardHeader><CardTitle className="flex items-center gap-2"><CheckCircle className="h-5 w-5 text-blue-500" />Clôturer une évolution</CardTitle></CardHeader><CardContent>
            <ol className="space-y-2">
              <GuideStep n={1}>Dans le tiroir, faire avancer la demande jusqu'à <strong>Testée</strong>.</GuideStep>
              <GuideStep n={2}>Cliquer sur <strong>« Passer à Déployée »</strong> : l'évolution atteint son <strong>état final</strong>.</GuideStep>
              <GuideStep n={3}>Une fois <strong>Déployée</strong>, elle compte dans le KPI « Déployées » et ne peut plus être rejetée.</GuideStep>
              <GuideStep n={4}>À tout moment avant le déploiement, une demande peut être <strong>Rejetée</strong> (abandon).</GuideStep>
            </ol>
          </CardContent></Card>
        </div>
      </div>

      {/* ===== Référence des champs ===== */}
      <Card><CardHeader><CardTitle className="flex items-center gap-2"><Info className="h-5 w-5 text-blue-500" />Référence des champs</CardTitle></CardHeader><CardContent>
        <div className="overflow-x-auto rounded-lg border border-gray-200 dark:border-gray-700">
          <table className="w-full text-sm">
            <thead className="bg-gray-50 dark:bg-gray-800 text-gray-600 dark:text-gray-300">
              <tr>{['Champ', 'Type', 'Requis', 'Détails'].map((h) => <th key={h} className="text-left font-semibold px-4 py-2">{h}</th>)}</tr>
            </thead>
            <tbody className="divide-y divide-gray-100 dark:divide-gray-800 text-gray-700 dark:text-gray-200">
              {[
                ['Titre', 'Texte', 'Oui', 'Intitulé court de la demande'],
                ['Fonction', 'Liste', 'Oui', 'Fonction métier rattachée'],
                ['Type', 'Liste', 'Non', '4 valeurs (Fonctionnalité, Amélioration, Optimisation, Refactoring)'],
                ['Priorité', 'Liste', 'Non', '4 niveaux (Critique, Élevée, Moyenne, Faible)'],
                ['Statut', 'Liste', 'Non', '8 valeurs du workflow (Demandée par défaut)'],
                ['Description', 'Texte long', 'Non', 'Détail du besoin'],
                ['Notes d\'implémentation', 'Texte long', 'Non', 'Renseignées à l\'étape Implémentée'],
              ].map((r, i) => (
                <tr key={i} className="hover:bg-gray-50 dark:hover:bg-gray-800/50">
                  <td className="px-4 py-2 font-medium">{r[0]}</td><td className="px-4 py-2">{r[1]}</td>
                  <td className="px-4 py-2">{r[2] === 'Oui' ? <Badge color="#EF4444">Oui</Badge> : <Badge>Non</Badge>}</td>
                  <td className="px-4 py-2 text-gray-600 dark:text-gray-300">{r[3]}</td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>

        {/* Statuts du workflow */}
        <h4 className="font-semibold text-gray-900 dark:text-white mt-5 mb-2">Statuts du workflow</h4>
        <div className="flex flex-wrap gap-2">
          {[...WORKFLOW, 'REJECTED'].map((s) => <StatusBadge key={s} status={s} />)}
        </div>
      </CardContent></Card>

      {/* ===== Règles de gestion + API ===== */}
      <div className="grid grid-cols-1 lg:grid-cols-2 gap-4">
        <Card><CardHeader><CardTitle>Règles de gestion</CardTitle></CardHeader><CardContent>
          <ul className="space-y-2 text-sm text-gray-700 dark:text-gray-300">
            <li className="flex items-start gap-2"><CheckCircle className="h-4 w-4 text-green-500 mt-0.5 shrink-0" /><span><strong>Titre</strong> et <strong>Fonction</strong> sont obligatoires à la création.</span></li>
            <li className="flex items-start gap-2"><CheckCircle className="h-4 w-4 text-green-500 mt-0.5 shrink-0" /><span>Toute nouvelle demande naît au statut <strong>Demandée</strong>.</span></li>
            <li className="flex items-start gap-2"><CheckCircle className="h-4 w-4 text-green-500 mt-0.5 shrink-0" /><span>L'<strong>approbation</strong> n'est possible que depuis le statut <strong>Demandée</strong>.</span></li>
            <li className="flex items-start gap-2"><CheckCircle className="h-4 w-4 text-green-500 mt-0.5 shrink-0" /><span>Le passage à <strong>Implémentée</strong> requiert des <strong>notes d'implémentation</strong>.</span></li>
            <li className="flex items-start gap-2"><XCircle className="h-4 w-4 text-red-500 mt-0.5 shrink-0" /><span>Une évolution <strong>Déployée</strong> est en état final : elle ne peut plus être rejetée.</span></li>
          </ul>
        </CardContent></Card>

        <Card><CardHeader><CardTitle>Endpoints API</CardTitle></CardHeader><CardContent>
          <ul className="space-y-1.5 text-sm font-mono text-gray-700 dark:text-gray-200">
            {[
              ['GET', '#10B981', '/api/automata/evolutions'],
              ['GET', '#10B981', '/api/automata/evolutions/{id}'],
              ['POST', '#3B82F6', '/api/automata/evolutions'],
              ['PUT', '#F59E0B', '/api/automata/evolutions/{id}'],
              ['DELETE', '#EF4444', '/api/automata/evolutions/{id}'],
              ['PATCH', '#8B5CF6', '/api/automata/evolutions/{id}/status?status='],
              ['PATCH', '#8B5CF6', '/api/automata/evolutions/{id}/assign?userId='],
              ['POST', '#3B82F6', '/api/automata/evolutions/{id}/approve'],
            ].map((r, i) => (
              <li key={i} className="flex items-center gap-2"><Badge color={r[1]}>{r[0]}</Badge><span className="break-all">{r[2]}</span></li>
            ))}
          </ul>
          <p className="text-xs text-gray-400 mt-3 flex items-center gap-1"><ArrowRight className="h-3 w-3" />Source : automata_environment / EvolutionController</p>
        </CardContent></Card>
      </div>
    </div>
  );
};

export default EvolutionsPOC;
